import type { Map as MapboxMap } from "mapbox-gl";

import { drawHandleRadii } from "./draw-styles";

/** Remove leftover Mapbox Draw layers/sources after the control is detached. */
export const removeLingeringDrawStyle = (map: MapboxMap): void => {
	const style = map.getStyle();
	if (!style) return;

	for (const layer of [...(style.layers ?? [])].reverse()) {
		if (layer.id.startsWith("gl-draw-") && map.getLayer(layer.id)) {
			map.removeLayer(layer.id);
		}
	}

	for (const sourceId of Object.keys(style.sources ?? {})) {
		if (
			(sourceId.startsWith("mapbox-gl-draw") ||
				sourceId.startsWith("gl-draw-")) &&
			map.getSource(sourceId)
		) {
			map.removeSource(sourceId);
		}
	}
};

/** Update live Draw layer paint without remounting the control. */
export const applyDrawAppearance = (
	map: MapboxMap,
	color: string,
	lineWidth: number,
	fillOpacity = 0,
): void => {
	const layers = map.getStyle()?.layers ?? [];
	for (const layer of layers) {
		if (!layer.id.startsWith("gl-draw-")) continue;

		if (layer.type === "fill") {
			map.setPaintProperty(layer.id, "fill-color", color);
			map.setPaintProperty(layer.id, "fill-opacity", fillOpacity);
			continue;
		}

		if (layer.type === "line") {
			map.setPaintProperty(layer.id, "line-color", color);
			map.setPaintProperty(layer.id, "line-dasharray", [1, 0]);
			map.setPaintProperty(layer.id, "line-width", lineWidth);
			map.setLayoutProperty(layer.id, "line-join", "round");
			map.setLayoutProperty(layer.id, "line-cap", "round");
			continue;
		}

		if (layer.type !== "circle") continue;

		const radii = drawHandleRadii(lineWidth);
		if (layer.id.includes("midpoint")) {
			map.setPaintProperty(layer.id, "circle-radius", radii.midpoint);
		} else if (layer.id.includes("outer")) {
			map.setPaintProperty(layer.id, "circle-radius", [
				"case",
				["==", ["get", "active"], "true"],
				radii.outerActive,
				radii.outer,
			]);
		} else {
			map.setPaintProperty(layer.id, "circle-radius", [
				"case",
				["==", ["get", "active"], "true"],
				radii.innerActive,
				radii.inner,
			]);
		}

		if (!layer.id.includes("outer")) {
			map.setPaintProperty(layer.id, "circle-color", color);
		}
	}
};
